/**
 * The bar that acts on every variant at once: schedule them all (optionally
 * staggered so a brand does not blast five networks in the same second) or
 * publish them one after another with live progress.
 */

import { useEffect, useMemo, useState } from 'react';
import { CalendarClock, Rocket } from 'lucide-react';
import { PLATFORM_SPECS } from '../../../shared/platforms';
import type { PlatformId, PlatformPost } from '../../../shared/types';
import { Badge, Button, DateTimePicker, Dialog, Field, PlatformIcon, Progress, Select } from '../ui';
import { formatWhen } from '../../lib/format';
import { cn } from '../../lib/utils';

export interface BulkProgress {
  running: boolean;
  done: number;
  total: number;
  current: PlatformId | null;
}

export interface BulkActionBarProps {
  /** Variants that can still be acted on (published ones are excluded). */
  variants: PlatformPost[];
  timezone: string;
  progress: BulkProgress;
  scheduling: boolean;
  /** Variants with blocking validation issues, by platform post id. */
  blockedIds: string[];
  onPublishAll: () => void;
  onScheduleAll: (startIso: string, staggerMinutes: number) => void;
  /** Seed for the schedule dialog — set when arriving from a calendar day. */
  defaultStartIso?: string | null;
  className?: string;
}

const STAGGER_OPTIONS = [
  { value: '0', label: 'All at the same time' },
  { value: '15', label: '15 minutes apart' },
  { value: '30', label: '30 minutes apart' },
  { value: '60', label: '1 hour apart' },
  { value: '1440', label: '1 day apart' },
];

export function BulkActionBar({
  variants,
  timezone,
  progress,
  scheduling,
  blockedIds,
  onPublishAll,
  onScheduleAll,
  defaultStartIso,
  className,
}: BulkActionBarProps) {
  const [open, setOpen] = useState(false);
  const [startIso, setStartIso] = useState<string | null>(null);
  const [stagger, setStagger] = useState('0');

  useEffect(() => {
    if (!open) return;
    setStartIso((current) => {
      if (current) return current;
      const existing = variants.find((variant) => variant.scheduledAt);
      if (existing?.scheduledAt) return new Date(existing.scheduledAt).toISOString();
      if (defaultStartIso && Date.parse(defaultStartIso) > Date.now()) return defaultStartIso;
      const soon = new Date(Date.now() + 60 * 60_000);
      soon.setMinutes(0, 0, 0);
      return soon.toISOString();
    });
  }, [open, variants, defaultStartIso]);

  const blocked = new Set(blockedIds);
  const publishable = variants.filter((variant) => !blocked.has(variant.id));
  const approved = variants.filter(
    (variant) => variant.status === 'approved' || variant.status === 'scheduled',
  ).length;
  const scheduled = variants.filter((variant) => variant.status === 'scheduled').length;

  // The preview must show exactly what will be scheduled — blocked variants are
  // skipped by the page, so listing them here would promise times that never
  // happen.
  const plan = useMemo(() => {
    const step = Number(stagger) || 0;
    const base = startIso ? Date.parse(startIso) : Number.NaN;
    return publishable.map((variant, index) => ({
      variant,
      at: Number.isNaN(base) ? null : base + index * step * 60_000,
    }));
  }, [publishable, startIso, stagger]);

  if (!variants.length) return null;

  return (
    <>
      <div
        className={cn(
          'pap-pop sticky bottom-4 z-20 flex flex-wrap items-center gap-x-4 gap-y-3 px-4 py-3',
          className,
        )}
      >
        {progress.running ? (
          <div className="min-w-0 flex-1 space-y-1.5">
            <p className="flex items-center gap-2 text-xs font-medium text-fg">
              {progress.current && <PlatformIcon platform={progress.current} size="xs" />}
              Publishing {progress.done + 1} of {progress.total}
              {progress.current ? ` · ${PLATFORM_SPECS[progress.current].shortName}` : ''}
            </p>
            <Progress value={progress.done} max={progress.total} label="Publishing progress" />
          </div>
        ) : (
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-fg">
              {variants.length} variant{variants.length === 1 ? '' : 's'} ready to go out
            </p>
            <p className="flex flex-wrap items-center gap-x-2 gap-y-1 pt-0.5 text-[11px] text-subtle">
              <span>{approved} approved</span>
              <span aria-hidden>·</span>
              <span>{scheduled} scheduled</span>
              {blocked.size > 0 && (
                <>
                  <span aria-hidden>·</span>
                  <Badge tone="danger" size="sm">
                    {blocked.size} need{blocked.size === 1 ? 's' : ''} attention
                  </Badge>
                </>
              )}
            </p>
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={progress.running}
            onClick={() => setOpen(true)}
            leftIcon={<CalendarClock className="size-3.5" />}
          >
            Schedule all
          </Button>
          <Button
            size="sm"
            disabled={progress.running || publishable.length === 0}
            loading={progress.running}
            onClick={onPublishAll}
            leftIcon={<Rocket className="size-3.5" />}
          >
            Publish all
            {publishable.length !== variants.length ? ` (${publishable.length})` : ''}
          </Button>
        </div>
      </div>

      <Dialog
        open={open}
        onOpenChange={setOpen}
        title="Schedule this post"
        description={`Times are in ${timezone}. Staggering spreads the variants out so they do not land together.`}
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={scheduling}
              disabled={!startIso || publishable.length === 0}
              onClick={() => {
                if (!startIso) return;
                onScheduleAll(startIso, Number(stagger) || 0);
                setOpen(false);
              }}
            >
              Schedule {publishable.length} variant{publishable.length === 1 ? '' : 's'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="First variant goes out">
            <DateTimePicker
              value={startIso}
              onChange={setStartIso}
              timezone={timezone}
              clearable={false}
              min={new Date(Date.now() + 60_000).toISOString()}
            />
          </Field>

          <Field label="Spacing">
            <Select
              value={stagger}
              onChange={(event) => setStagger(event.target.value)}
              options={STAGGER_OPTIONS}
              aria-label="Time between variants"
            />
          </Field>

          <ul className="divide-y divide-border rounded-lg border border-border">
            {plan.map(({ variant, at }) => (
              <li key={variant.id} className="flex items-center gap-3 px-3 py-2">
                <PlatformIcon platform={variant.platform} size="sm" variant="soft" />
                <span className="min-w-0 flex-1 truncate text-xs font-medium text-fg">
                  {PLATFORM_SPECS[variant.platform].shortName}
                </span>
                <span className="pap-tnum text-xs text-muted">
                  {at ? formatWhen(at, timezone) : '—'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </Dialog>
    </>
  );
}
